/*
 * Feedback bridge: call dialogs and toasts from anywhere (components, hooks, plain modules)
 * without the browser's native alert(), confirm() or prompt().
 *
 *   import { toast, confirmDialog, promptDialog, alertDialog } from '../ui/feedback';
 *
 *   toast.success('Saved');                       // non-blocking
 *   toast.error('Could not reach Ejar');
 *   if (!(await confirmDialog({ title: 'Delete 3 contracts?', confirmLabel: 'Delete', danger: true }))) return;
 *   const reason = await promptDialog({ title: 'Reason', label: 'Reason' });   // string | null
 *   await alertDialog({ title: 'Import finished', message: '120 rows added' });
 *
 * ModalProvider registers the real implementation on mount; calls made before that are queued.
 */

let bridge = null;
const pending = [];

export function setFeedbackBridge(impl) {
    bridge = impl;
    if (impl) {
        while (pending.length) {
            const job = pending.shift();
            job();
        }
    }
}

const run = (fn) => new Promise((resolve) => {
    const job = () => resolve(fn(bridge));
    if (bridge) job();
    else pending.push(job);
});

const toText = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (value instanceof Error) return value.message || String(value);
    try { return JSON.stringify(value); } catch (e) { return String(value); }
};

/** Non-blocking toast. type: success | error | info | warning. */
export function toast(message, type = 'info', duration) {
    run((b) => b.toast(toText(message), type, duration));
}
toast.success = (message, duration) => toast(message, 'success', duration);
toast.error = (message, duration) => toast(message, 'error', duration ?? 6000);
toast.info = (message, duration) => toast(message, 'info', duration);
toast.warning = (message, duration) => toast(message, 'warning', duration ?? 5000);

const normalise = (opts, fallbackTitle) => (
    typeof opts === 'string' || opts instanceof Error
        ? { title: fallbackTitle, message: toText(opts) }
        : { ...opts, message: toText(opts && opts.message) }
);

/** Confirmation dialog. Resolves true when confirmed, false when cancelled or dismissed. */
export function confirmDialog(opts) {
    const o = normalise(opts, 'Are you sure?');
    return run((b) => b.confirm(o));
}

/** Prompt dialog. Resolves the entered text, or null when cancelled. */
export function promptDialog(opts) {
    const o = normalise(opts, 'Enter a value');
    return run((b) => b.prompt(o));
}

/** Blocking message dialog with a single OK button. Resolves when dismissed. */
export function alertDialog(opts) {
    const o = normalise(opts, 'Notice');
    return run((b) => b.alert(o));
}

export const notify = toast;
